/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import {
  Avatar,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import { useController, useFormContext } from 'react-hook-form';
import { useGetBadgeList } from '../../../features/booking/api/master-data/use-get-badge-list';
import type { RatingFormData } from './types';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export function BadgeSection() {
  const { control } = useFormContext<RatingFormData>();
  const {
    field: { value: selectedBadges, onChange },
  } = useController({ name: 'selectedBadges', control });

  const { orderDetails: order } = useBookingStore((state) => state);

  const { data: badgeList, isLoading: isLoadingBadges } = useGetBadgeList({
    delivery_type: order?.delivery_type || order?.service.delivery_type || '',
  });

  const handleBadgeSelect = (badgeCode: string) => {
    if (selectedBadges.includes(badgeCode)) {
      onChange(selectedBadges.filter((id) => id !== badgeCode));
    } else {
      onChange([...selectedBadges, badgeCode]);
    }
  };

  return (
    <Stack gap={rem(16)} p={rem(16)} w="100%">
      <Text fz={rem(16)} fw={600} c="neutral.7">
        Bạn hài lòng về điều gì?
      </Text>

      <ScrollArea
        mih={rem(120)}
        scrollbars="x"
        type="hover"
        style={{ width: '100%' }}
        styles={{
          viewport: {
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-x',
          },
        }}
      >
        <Group
          gap={rem(12)}
          wrap="nowrap"
          style={{
            width: 'max-content',
          }}
        >
          {isLoadingBadges
            ? Array.from({ length: 4 }).map((_, index) => (
                <Stack
                  key={index}
                  justify="center"
                  align="center"
                  gap={rem(4)}
                  style={{ minWidth: rem(84) }}
                >
                  <Skeleton height={rem(60)} width={rem(60)} circle />
                  <Skeleton height={rem(16)} width={rem(70)} />
                </Stack>
              ))
            : badgeList?.map((badge) => {
                const isSelected = selectedBadges.includes(badge.code);
                return (
                  <Stack
                    key={badge.index}
                    justify="center"
                    align="center"
                    gap={rem(4)}
                    mih={rem(108)}
                    w={rem(84)}
                    style={{
                      cursor: 'pointer',
                      padding: rem(8),
                      borderRadius: rem(8),
                      transition: 'all 0.2s ease-in-out',
                      transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    }}
                    onClick={() => handleBadgeSelect(badge.code)}
                  >
                    <Avatar
                      src={badge.image_url}
                      alt={badge.name.vi}
                      style={{
                        width: isSelected ? rem(64) : rem(60),
                        height: isSelected ? rem(64) : rem(60),
                        transition: 'all 0.2s ease-in-out',
                      }}
                    />
                    <Text
                      size={isSelected ? 'md' : 'sm'}
                      fw={isSelected ? 600 : 500}
                      c="neutral.7"
                      ta="center"
                      lineClamp={2}
                      w="100%"
                      style={{ transition: 'all 0.2s ease-in-out' }}
                    >
                      {badge.name.vi}
                    </Text>
                  </Stack>
                );
              })}
        </Group>
      </ScrollArea>
    </Stack>
  );
}
